/**
 * CashFlowTable — multi-year Cash Flow Statement table.
 *
 * PDF-ingested companies carry a CashFlowStatement node; scraped India
 * companies do not. The component renders nothing when no year has any
 * cash-flow figure, so it can sit unconditionally in the Finance section and
 * appear only for companies that actually have the statement.
 */
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear, formatCompactCurrency, formatUnitLabel } from '../utils/formatters';
import { resolveRecordValue } from '../utils/extendedFields';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function CashFlowTable({ data, region }: Props) {
  const years = data;
  const unitLabel = formatUnitLabel(region);

  type RowDef = {
    label: string;
    /** Every spelling the value may arrive under (DTO field or extended_fields). */
    keys: string[];
    indent?: boolean;
    bold?: boolean;
    separator?: boolean;
  };

  const rows: RowDef[] = [
    { label: 'Operating Cash Flow', keys: ['operating_cash_flow', 'Operating_Cash_Flow'], bold: true },
    { label: 'Investing Cash Flow', keys: ['investing_cash_flow', 'Investing_Cash_Flow'], bold: true },
    { label: 'Financing Cash Flow', keys: ['financing_cash_flow', 'Financing_Cash_Flow'], bold: true },
    { label: '', keys: [], separator: true },
    { label: 'Capital Expenditure', keys: ['capital_expenditure', 'Capital_Expenditure'], indent: true },
    { label: 'Dividends Paid', keys: ['dividends_paid', 'Dividends_Paid'], indent: true },
    { label: '', keys: [], separator: true },
    { label: 'Free Cash Flow', keys: ['free_cash_flow', 'Free_Cash_Flow'], bold: true },
    { label: 'Net Change in Cash', keys: ['net_change_in_cash', 'Net_Change_In_Cash'], bold: true },
  ];

  const valueOf = (d: UnifiedFinancialRecord, row: RowDef) =>
    row.keys.length > 0 ? resolveRecordValue(d, ...row.keys) : null;

  // No cash-flow statement at all (scraped India) — render nothing.
  const hasAnyCashFlow = rows.some(
    (row) => !row.separator && years.some((d) => valueOf(d, row) != null)
  );
  if (!hasAnyCashFlow) return null;

  const fmt = (val: number | null) => {
    if (val == null || isNaN(val)) return '—';
    return formatCompactCurrency(val, region);
  };

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-neutral-800">Cash Flow Statement</h3>
        {unitLabel && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 bg-neutral-100 border border-neutral-200 rounded px-2 py-1 whitespace-nowrap">
            {unitLabel}
          </span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                <th className="text-left py-2.5 px-3 text-neutral-500 font-semibold text-[11px] uppercase tracking-wider min-w-[12rem] sticky left-0 bg-neutral-50 z-10">
                  Line Item
                </th>
                {years.map((d) => (
                  <th
                    key={d.year}
                    className="text-right py-2.5 px-3 text-neutral-700 font-bold text-[11px] whitespace-nowrap min-w-[5rem]"
                  >
                    {formatFiscalYear(d.year, region)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                if (row.separator) {
                  return (
                    <tr key={idx}>
                      <td colSpan={years.length + 1} className="py-0.5 bg-neutral-100/70" />
                    </tr>
                  );
                }
                const hasAnyValue = years.some((d) => valueOf(d, row) != null);
                if (!hasAnyValue) return null;
                const rowBg = row.bold ? 'bg-neutral-50/80' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/30';
                const labelBg = row.bold ? 'bg-neutral-50/95' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60';
                return (
                  <tr
                    key={idx}
                    className={`border-b border-neutral-100 transition-colors hover:bg-violet-50/40 ${rowBg}`}
                  >
                    <td
                      className={`py-2 px-3 text-xs leading-snug sticky left-0 z-10 ${labelBg} ${
                        row.bold ? 'font-semibold text-neutral-800' : 'text-neutral-600'
                      } ${row.indent ? 'pl-7' : ''}`}
                    >
                      {row.label}
                    </td>
                    {years.map((d) => {
                      const val = valueOf(d, row);
                      const formatted = fmt(val);
                      const isNeg = typeof val === 'number' && val < 0;
                      return (
                        <td
                          key={d.year}
                          className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${
                            isNeg ? 'text-red-600' : row.bold ? 'font-semibold text-neutral-900' : 'text-neutral-700'
                          } ${row.bold ? 'font-semibold' : ''}`}
                        >
                          {formatted}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
